import { FC } from 'react';
import dayjs from 'dayjs';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { selectResult } from 'store/player/playerSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
import QuizTypes from 'types/QuizTypes';
import IQuiz from 'models/Quiz';

import classes from './styles.module.css';
import useCompactLayout from 'hooks/useCompactLayout';

type Props = {
  hideTitle?: boolean;
}

const AppBar:FC<Props> = ({ hideTitle = false }) => {
  const { t } = useTranslation();
  const isCompact = useCompactLayout();

  const result:any = useAppSelector(selectResult);
  const storeQuiz = useAppSelector(selectQuiz);
  const quiz:IQuiz | null = storeQuiz || result?.quiz || null;

  const quizName = result.quizGroup ? result.quizGroup.name : quiz?.quizName;
  const title = quiz
    ? quiz.publicLeaderboard.headerText || `${quizName} ${t('pages.leaderboard.title')}`
    : t('pages.leaderboard.leagueTitle');

  const quizEnded = dayjs().isAfter(Number(quiz?.endsDate));
  
  const logoImage = result.quizGroup ? result.quizGroup.logoImage : quiz?.logoImage;
  const logoBgColor = result.quizGroup ? result.quizGroup.logoImageBackgroundColor : quiz?.logoImageBackgroundColor;
  const primaryColor = result.quizGroup ? result.quizGroup.publicLeaderboard.primaryColor
    : quiz?.publicLeaderboard.primaryColor;

  if (!result) return null;

  const logos = (
    <>
      <img src={logoImage} alt="logo" className={`${classes.logo} shadow-primary rounded-lg`} style={{ backgroundColor: logoBgColor }} />
      {quiz?.sponsorImage ? (
        <img src={quiz.sponsorImage} alt="sponsor-logo" className={`${classes.logo} shadow-primary rounded-lg`} style={{ backgroundColor: quiz?.sponsorImageBackgroundColor || 'none' }} />
      ) : null}
    </>
  );

  if (isCompact && hideTitle) {
    const hasTwoLogos = !!quiz?.sponsorImage;
    return (
      <div
        className={`relative z-40 overflow-hidden shrink-0 flex items-center gap-3 ${hasTwoLogos ? 'justify-between' : 'justify-center'}`}
        style={{ fontFamily: 'Montserrat' }}
      >
        {logos}
      </div>
    )
  }

  return (
    <div
      className={`
        relative z-40 overflow-hidden shrink-0
        flex items-center justify-between gap-4
        ${isCompact ? 'flex-row py-0 mb-0' : 'flex-row portrait:flex-col py-2 mb-4'}
      `}
      style={{ fontFamily: 'Montserrat' }}
    >
      <div className="flex-1 min-w-0">
        <h3
          className={`font-extrabold uppercase ${isCompact ? 'text-xl truncate text-left' : 'text-xl sm:text-5xl text-center'}`}
          style={{ color: primaryColor }}
        >
          {title}
        </h3>
        {quiz && quiz.type === QuizTypes.Prediction && quizEnded && !isCompact && (
          <p className="text-white text-center ml-1 sm:text-md">
            {result?.quiz.numberOfQuestions === result?.quiz.numberOfAnsweredQuestions
              ? t('pages.leaderboard.finalResults')
              : t('pages.leaderboard.answeredQuestions', { questions: result?.quiz.numberOfQuestions, answeredQuestions: result?.quiz.numberOfAnsweredQuestions })
            }
          </p>
        )}
      </div>
      <div className={`flex-1 flex justify-around shrink-0 ${isCompact ? 'gap-3' : 'gap-8'}`}>
        {logos}
      </div>
    </div>
  )
}

export default AppBar;
